/**
 * Combined Locale Selector Component
 * Handles country and language selection with smooth transitions
 * Uses modern fetch API to update localization without full page reloads
 */

class CombinedLocaleForm extends HTMLElement {
  constructor() {
    super();
    
    this.elements = {
      button: this.querySelector('[data-toggle-button]'),
      panel: this.querySelector('.disclosure-list'),
      options: this.querySelectorAll('[data-locale-option]'),
      countryInput: this.querySelector('[data-country-input]'),
      localeInput: this.querySelector('[data-locale-input]'),
    };
    
    // Find forms by traversing from inputs
    this.elements.countryForm = this.elements.countryInput?.closest('form');
    this.elements.localeForm = this.elements.localeInput?.closest('form');

    this.currentCountry = this.dataset.currentCountry || '';
    this.currentLocale = this.dataset.currentLocale || '';
    this.isUpdating = false;
    this.updateTimeout = null; // For safety timeout

    // Create ARIA live region for screen readers
    this.createAriaLiveRegion();

    this.init();
  }

  createAriaLiveRegion() {
    // Create hidden ARIA live region for screen reader announcements
    this.ariaLiveRegion = document.createElement('div');
    this.ariaLiveRegion.className = 'visually-hidden';
    this.ariaLiveRegion.setAttribute('role', 'status');
    this.ariaLiveRegion.setAttribute('aria-live', 'polite');
    this.ariaLiveRegion.setAttribute('aria-atomic', 'true');
    this.appendChild(this.ariaLiveRegion);
  }

  announceToScreenReader(message) {
    if (this.ariaLiveRegion) {
      // Clear previous message
      this.ariaLiveRegion.textContent = '';
      // Set new message after a brief delay to ensure it's announced
      setTimeout(() => {
        this.ariaLiveRegion.textContent = message;
      }, 100);
    }
  }

  init() {    
    // Warn if forms not found
    if (!this.elements.countryForm || !this.elements.localeForm) {
      console.warn('⚠️ Localization forms not found! Will use fallback method.');
    }

    // Setup event listeners
    if (this.elements.button) {
      this.togglePanelBound = this.togglePanel.bind(this);
      this.elements.button.addEventListener('click', this.togglePanelBound);
    } else {
      console.error('Toggle button not found!');
    }

    this.handleOptionClickBound = this.handleOptionClick.bind(this);
    this.elements.options.forEach(option => {
      option.addEventListener('click', this.handleOptionClickBound);
    });

    // Close on outside click
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);
    
    // Close on escape key
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    document.addEventListener('keydown', this.handleEscapeKey);

    // Reset isUpdating on page visibility change or navigation
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // Handle dropdown positioning
    this.handleDropdownPosition();
    
    // Reposition on window resize
    this.handleDropdownPositionBound = this.handleDropdownPosition.bind(this);
    window.addEventListener('resize', this.handleDropdownPositionBound);
  }

  handleDropdownPosition() {
    if (!this.elements.button || !this.elements.panel) return;
    
    const offsetButton = this.elements.button.getBoundingClientRect().right;
    const dropdownWidth = 280; // matches --f-max-width
    
    if (window.innerWidth - offsetButton < dropdownWidth && matchMedia('(min-width: 768px)').matches) {
      this.elements.panel.classList.add('disclosure-list__right');
    } else {
      this.elements.panel.classList.remove('disclosure-list__right');
    }
  }

  togglePanel(event) {
    // Prevent accidental navigation if the toggle is an <a>
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    const isExpanded = this.elements.button.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;
    
    this.elements.button.setAttribute('aria-expanded', newState.toString());
    
    // Use both class and attribute for compatibility
    if (newState) {
      this.classList.add('is-open');
      this.setAttribute('open', '');
    } else {
      this.classList.remove('is-open');
      this.removeAttribute('open');
    }
  }

  closePanel() {
    if (this.elements.button) {
      this.elements.button.setAttribute('aria-expanded', 'false');
    }
    this.classList.remove('is-open');
    this.removeAttribute('open');
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.closePanel();
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      this.closePanel();
      if (this.elements.button) {
        this.elements.button.focus();
      }
    }
  }

  handlePageShow(event) {
    // Reset isUpdating when page is shown (e.g., back button navigation)
    const isBackForward = event.persisted || 
     (window.performance && 
      performance.getEntriesByType && 
      performance.getEntriesByType('navigation')[0]?.type === 'back_forward');
    
    if (isBackForward) {
      this.resetUpdatingState();
    }
  }

  handleBeforeUnload() {
    // Clear any pending timeout before page unload
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  }

  resetUpdatingState() {
    this.isUpdating = false;
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
    // Remove loading state
    const container = this.closest('.combined-locale-selector');
    if (container) {
      container.classList.remove('is-loading');
    }
    // Clear ARIA announcement
    if (this.ariaLiveRegion) {
      this.ariaLiveRegion.textContent = '';
    }
  }

  validateLocaleCode(code) {
    // Validate locale code format
    if (!code || typeof code !== 'string') return false;

    return /^[a-z]{2,3}(-[A-Z][a-z]{3})?(-[A-Z]{2})?(-[a-zA-Z0-9]{1,8})*$/i.test(code);
  }

  validateCountryCode(code) {
    // Validate country code format (e.g., 'US', 'CA', 'BR')
    // ISO 3166-1 alpha-2 two-letter codes
    if (!code || typeof code !== 'string') return false;
    return /^[A-Z]{2}$/i.test(code);
  }
  
  normalizeLocaleCode(code) {
    // Normalize locale code to canonical form
    if (!code || typeof code !== 'string') return code;
    
    const parts = code.split('-');
    if (parts.length === 1) {
      // Simple language code: lowercase
      return parts[0].toLowerCase();
    }
    
    // Normalize each part according to BCP 47:
    return parts.map((part, index) => {
      if (index === 0) {
        // Language: lowercase
        return part.toLowerCase();
      } else if (part.length === 4 && /^[a-z]{4}$/i.test(part)) {
        // Script: Title case (first uppercase, rest lowercase)
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      } else if (part.length === 2 && /^[a-z]{2}$/i.test(part)) {
        // Region: uppercase
        return part.toUpperCase();
      } else {
        // Variant or other: lowercase
        return part.toLowerCase();
      }
    }).join('-');
  }

  showError(message) {
    console.error('Localization error:', message);
    this.announceToScreenReader(message);
    
    // Reset state
    this.resetUpdatingState();
    
    // Optional: Show a user-friendly notification
    // You can integrate with your theme's notification system here
    alert(message); // Fallback for now
  }

  async handleOptionClick(event) {
    if (this.isUpdating) return;
    // Prevent accidental navigation if the option is an <a>
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    const option = event.currentTarget;
    const selectedCountry = option.dataset.country;
    const selectedLocale = option.dataset.locale;

    // Validate input codes
    if (!this.validateCountryCode(selectedCountry)) {
      this.showError('Invalid country code format. Please try again.');
      return;
    }

    if (!this.validateLocaleCode(selectedLocale)) {
      this.showError('Invalid language code format. Please try again.');
      return;
    }

    // Normalize locale codes for comparison
    const normalizedSelectedLocale = this.normalizeLocaleCode(selectedLocale || '');
    const normalizedCurrentLocale = this.normalizeLocaleCode(this.currentLocale || '');
    const normalizedSelectedCountry = selectedCountry?.toUpperCase() || '';
    const normalizedCurrentCountry = this.currentCountry?.toUpperCase() || '';

    // Check if already current (using normalized values)
    if (normalizedSelectedCountry === normalizedCurrentCountry && 
        normalizedSelectedLocale === normalizedCurrentLocale) {
      this.closePanel();
      return;
    }

    try {
      this.isUpdating = true;
      this.showLoading();

      // Set a safety timeout (5 seconds) to reset isUpdating in case navigation fails
      this.updateTimeout = setTimeout(() => {
        console.warn('Localization update timeout - resetting state');
        this.showError('Update is taking longer than expected. Please try again.');
      }, 5000);

      // Use form submission method (most reliable for Shopify)
      // Pass normalized values for consistency
      this.submitLocalizationForm(normalizedSelectedCountry, normalizedSelectedLocale);
    } catch (error) {
      console.error('Error during localization update:', error);
      this.showError('An error occurred while updating your location. Please try again.');
    }
  }

  submitLocalizationForm(countryCode, localeCode) {
    try {
      // Normalize for consistent comparison
      // Country codes are always uppercase
      const normalizedCurrentCountry = (this.currentCountry || '').toUpperCase();
      const normalizedCountryCode = (countryCode || '').toUpperCase();
      
      // Locale codes use BCP 47 canonical form (e.g., 'en-US', 'zh-Hans-CN')
      const normalizedCurrentLocale = this.normalizeLocaleCode(this.currentLocale || '');
      const normalizedLocaleCode = this.normalizeLocaleCode(localeCode || '');
      
      const countryChanged = normalizedCountryCode && normalizedCountryCode !== normalizedCurrentCountry;
      const localeChanged = normalizedLocaleCode && normalizedLocaleCode !== normalizedCurrentLocale;
      
      // If both changed, we need to handle them together
      // Shopify accepts both country_code and locale_code in the same form
      if (countryChanged && localeChanged) {
        // Use country form and add locale to it
        if (this.elements.countryForm && this.elements.countryInput) {
          // Use normalized values for form submission
          this.elements.countryInput.value = normalizedCountryCode;
          
          // Check if locale input exists in this form
          let localeInputInForm = this.elements.countryForm.querySelector('[name="locale_code"]');
          if (!localeInputInForm) {
            // Create locale input in the country form
            localeInputInForm = document.createElement('input');
            localeInputInForm.type = 'hidden';
            localeInputInForm.name = 'locale_code';
            this.elements.countryForm.appendChild(localeInputInForm);
          }
          localeInputInForm.value = normalizedLocaleCode;
          
          this.elements.countryForm.submit();
          return;
        }
      }
      
      // If only country changed
      if (countryChanged && this.elements.countryForm && this.elements.countryInput) {
        this.elements.countryInput.value = normalizedCountryCode;
        this.elements.countryForm.submit();
        return;
      }
      
      // If only locale changed
      if (localeChanged && this.elements.localeForm && this.elements.localeInput) {
        this.elements.localeInput.value = normalizedLocaleCode;
        this.elements.localeForm.submit();
        return;
      }
      
      // Method 2: Fallback - Use URL parameters
      if (countryChanged || localeChanged) {
        // Pass normalized values to URL method
        this.updateViaURL(normalizedCountryCode, normalizedLocaleCode);
        return;
      }
      
      this.resetUpdatingState();
      this.closePanel();
    } catch (error) {
      console.error('Error submitting localization form:', error);
      this.showError('Failed to update localization. Please try again.');
    }
  }
  
  updateViaURL(countryCode, localeCode) {
    try {
      // Build URL with locale and country parameters
      const url = new URL(window.location.href);
      
      // Normalize for comparison
      const normalizedCurrentLocale = this.normalizeLocaleCode(this.currentLocale || '');
      const normalizedLocaleCode = this.normalizeLocaleCode(localeCode || '');
      const normalizedCurrentCountry = (this.currentCountry || '').toUpperCase();
      const normalizedCountryCode = (countryCode || '').toUpperCase();
      
      // Set locale parameter (Shopify recognizes this in normalized form)
      if (normalizedLocaleCode && normalizedLocaleCode !== normalizedCurrentLocale) {
        url.searchParams.set('locale', normalizedLocaleCode);
      }
      
      // Set country parameter
      if (normalizedCountryCode && normalizedCountryCode !== normalizedCurrentCountry) {
        url.searchParams.set('country', normalizedCountryCode);
      }
      
      window.location.href = url.toString();
    } catch (error) {
      console.error('Error updating via URL:', error);
      this.showError('Failed to redirect. Please try again.');
    }
  }


  showLoading() {
    // Add loading class to the main container
    const container = this.closest('.combined-locale-selector');
    if (container) {
      container.classList.add('is-loading');
    }
    
    // Announce to screen readers
    this.announceToScreenReader('Updating localization. Please wait...');
    
    this.closePanel();
  }

  disconnectedCallback() {
    // Cleanup event listeners
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscapeKey);
    window.removeEventListener('resize', this.handleDropdownPositionBound);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    if (this.elements.button && this.togglePanelBound) {
      this.elements.button.removeEventListener('click', this.togglePanelBound);
    }

    if (this.elements.options && this.handleOptionClickBound) {
      this.elements.options.forEach(option => {
        option.removeEventListener('click', this.handleOptionClickBound);
      });
    }
    
    // Clear any pending timeouts
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  }
}

// Register the custom element
if (!customElements.get('combined-locale-form')) {
  customElements.define('combined-locale-form', CombinedLocaleForm);
}
